/**
 * PlansView — Obsidian Companion "Plans" screen.
 *
 * Shows the user's current entitlement (from useAccount) alongside a
 * Free vs. Loaf Pro comparison grid. Billing is external: the Pro card
 * CTA deep-links to the Google Play Store listing, where upgrades and
 * subscription management happen.
 */
import TopAppBar, { AccountActionButton } from '../components/TopAppBar';
import Icon from '../components/Icon';
import { useAccount } from '../auth/AccountContext';
import { PLAY_STORE_URL } from '../lib/links';

interface PlanFeature {
  icon: string;
  title: string;
  subtitle: string;
  fill?: boolean;
}

const FREE_FEATURES: PlanFeature[] = [
  { icon: 'mic', title: 'Voice typing', subtitle: 'Dictate text with the Loaf Android keyboard.' },
  { icon: 'edit_document', title: 'Available writing tools', subtitle: 'Use available writing features in the Loaf Android keyboard.' },
  { icon: 'history', title: 'History', subtitle: 'Review the transcription history available to your account.' },
  { icon: 'library_books', title: 'Snippets', subtitle: 'Save trigger-based text expansions for the keyboard.' },
  { icon: 'devices', title: 'Android app', subtitle: 'Voice input and writing tools run from the Android keyboard.' },
];

const PRO_FEATURES: PlanFeature[] = [
  { icon: 'edit_document', title: 'Additional AI writing tools', subtitle: 'Rewrite, proofread, and compose text with AI.', fill: true },
];

function PlanFeatureRow({ feature, accent }: { feature: PlanFeature; accent: 'muted' | 'primary' }) {
  const iconColor = accent === 'primary' ? 'text-primary' : 'text-on-surface-variant';
  return (
    <div className="flex items-start gap-3">
      <Icon name={feature.icon} fill={feature.fill} className={`${iconColor} mt-0.5`} />
      <div>
        <p className="font-title-md text-title-md text-on-surface">{feature.title}</p>
        <p className="font-body-sm text-body-sm text-on-surface-variant">{feature.subtitle}</p>
      </div>
    </div>
  );
}

function CurrentPlanCard({ isPro }: { isPro: boolean }) {
  return (
    <section className="flex flex-col justify-between gap-4 rounded-xl bg-surface-container-low p-5 ring-1 ring-outline-variant/35 md:flex-row md:items-center">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-primary-container/20 text-primary flex items-center justify-center">
          <Icon name="workspace_premium" fill className="text-[32px]" />
        </div>
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider mb-1">
            Current plan
          </p>
          <h2 className="font-display-md text-display-md text-on-surface flex items-center gap-2">
            {isPro ? 'Loaf Pro' : 'Free'}
            {isPro && (
              <span className="px-2 py-0.5 rounded-full bg-primary-container text-on-primary-container font-label-sm text-label-sm ml-2">
                ACTIVE
              </span>
            )}
          </h2>
        </div>
      </div>
      <div className="text-left md:text-right">
        <p className="font-body-md text-body-md text-on-surface-variant">
          {isPro ? 'Pro access active' : 'No active subscription'}
        </p>
        <p className="font-title-md text-title-md text-on-surface">
          {isPro ? '—' : 'Included features'}
        </p>
      </div>
    </section>
  );
}

function FreeTierCard() {
  return (
    <div className="relative flex flex-col rounded-xl bg-surface-container-low p-5 ring-1 ring-outline-variant/25">
      <div className="mb-6">
        <h3 className="font-headline-lg text-headline-lg text-on-surface mb-2">Free</h3>
        <p className="font-body-md text-body-md text-on-surface-variant">
          Essential voice typing features for casual use.
        </p>
      </div>
      <div className="flex-1 space-y-4">
        {FREE_FEATURES.map((f) => (
          <PlanFeatureRow key={f.title} feature={f} accent="muted" />
        ))}
      </div>
    </div>
  );
}

function ProTierCard({ isPro }: { isPro: boolean }) {
  return (
    <div className="relative flex flex-col rounded-xl bg-surface-container p-5 ring-1 ring-primary/60">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-on-primary font-label-sm text-label-sm px-3 py-1 rounded-full uppercase tracking-widest font-bold shadow-lg">
        Recommended
      </div>
      <div className="mb-6 mt-2">
        <h3 className="font-headline-lg text-headline-lg text-primary mb-2 flex items-center gap-2">
          Loaf Pro
          <Icon name="verified" fill className="text-primary text-[24px]" />
        </h3>
        <p className="font-body-md text-body-md text-on-surface-variant">
          Add additional AI writing tools in the Loaf Android keyboard.
        </p>
      </div>
      <div className="flex-1 space-y-4 mb-8">
        {PRO_FEATURES.map((f) => (
          <PlanFeatureRow key={f.title} feature={f} accent="primary" />
        ))}
      </div>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-h-11 w-full items-center justify-center rounded-lg bg-primary px-4 font-title-md text-title-md text-on-primary transition-colors hover:bg-primary/90 active:scale-[0.98]"
      >
        {isPro ? 'Manage on Google Play' : 'Upgrade on Google Play'}
        <Icon name="open_in_new" className="ml-2" />
      </a>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-gap-section">
      <div className="h-28 rounded-2xl bg-surface-container-low border border-outline-variant/30 animate-pulse" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-gap-stack">
        <div className="h-[420px] rounded-2xl bg-surface-container-low border border-outline-variant/20 animate-pulse" />
        <div className="h-[420px] rounded-2xl bg-surface-container border border-primary/50 animate-pulse" />
      </div>
    </div>
  );
}

export default function PlansView() {
  const { user, loading, error } = useAccount();
  const isPro = user?.is_pro ?? false;

  return (
    <>
      <TopAppBar
        title="Plans"
        backTo="/app/account"
        breadcrumb={[{ label: 'Settings' }, { label: 'Account', to: '/app/account' }]}
        actions={<AccountActionButton />}
      />
      <div className="pt-24 pb-24 px-gutter md:px-8 max-w-[900px] w-full mx-auto flex flex-col gap-gap-section">
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="bg-error-container/20 border border-outline-variant/30 rounded-2xl p-6 flex items-start gap-3">
            <Icon name="error" className="text-error mt-0.5" />
            <div>
              <p className="font-title-md text-title-md text-on-surface">Couldn’t load your plan</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant">{error}</p>
            </div>
          </div>
        ) : (
          <>
            <CurrentPlanCard isPro={isPro} />
            <section className="grid grid-cols-1 md:grid-cols-2 gap-gap-stack">
              <FreeTierCard />
              <ProTierCard isPro={isPro} />
            </section>
            <div className="text-center">
              <p className="font-body-sm text-body-sm text-on-surface-variant flex items-center justify-center gap-2">
                <Icon name="info" className="text-[16px]" />
                Subscription changes and billing are managed through the Loaf Android app or Google Play Store.
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
}
